import { useQuery } from '@tanstack/react-query';

import { fetchGroupedNotifications, fetchOffsetNotifications } from '../services';
import type {
  GroupedNotification,
  NotificationRow,
  NotificationStatusFilter,
  Pagination,
} from '../types';
import { type TimePreset, timePresetToCreatedAfter } from '../utils';

function groupedToRows(groups: GroupedNotification[]): NotificationRow[] {
  return groups.map(group => {
    if (group.count === 1 && group.notifications.length === 1) {
      const n = group.notifications[0];
      return {
        id: n.id,
        group_id: n.group_id,
        type: n.type,
        title: n.title,
        message: n.message,
        agent_id: n.agent_id,
        is_read: n.is_read,
        created_at: n.created_at,
        count: 1,
        unread_count: n.is_read ? 0 : 1,
        subRows: [],
        notification: n,
      };
    }

    return {
      id: group.group_id,
      group_id: group.group_id,
      type: group.type,
      title: group.title,
      message: '',
      agent_id: group.agent_id,
      is_read: group.is_read,
      created_at: group.latest_at,
      count: group.count,
      unread_count: group.unread_count,
      subRows: group.notifications.map(n => ({
        id: n.id,
        group_id: n.group_id,
        type: n.type,
        title: n.title,
        message: n.message,
        agent_id: n.agent_id,
        is_read: n.is_read,
        created_at: n.created_at,
        count: 1,
        unread_count: n.is_read ? 0 : 1,
        subRows: [],
        notification: n,
      })),
      notification: null,
    };
  });
}

type UseOffsetNotificationListParams = {
  page: number;
  pageSize: number;
  status: NotificationStatusFilter;
  agentId?: string;
  category?: string;
  priority?: string;
  time?: TimePreset;
  grouped?: boolean;
};

type UseOffsetNotificationListResult = {
  rows: NotificationRow[];
  pagination: Pagination | undefined;
};

export function useOffsetNotificationList(params: UseOffsetNotificationListParams) {
  const { page, pageSize, status, agentId, category, priority, time, grouped = false } = params;

  return useQuery<UseOffsetNotificationListResult>({
    queryKey: [
      'notifications',
      'offset-list',
      page,
      pageSize,
      status,
      agentId,
      category,
      priority,
      time,
      grouped,
    ],
    queryFn: async () => {
      const is_read = status === 'read' ? true : status === 'unread' ? false : undefined;
      const created_after = time ? timePresetToCreatedAfter(time) : undefined;
      const fetchParams = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        is_read,
        agent_id: agentId,
        category,
        priority,
        created_after,
      };

      if (grouped) {
        const data = await fetchGroupedNotifications(fetchParams);
        return { rows: groupedToRows(data.items), pagination: data.pagination };
      }

      const data = await fetchOffsetNotifications(fetchParams);
      return {
        rows: data.items.map(
          (n): NotificationRow => ({
            id: n.id,
            group_id: n.group_id ?? n.id,
            type: n.type,
            title: n.title,
            message: n.message,
            agent_id: n.agent_id,
            is_read: n.is_read,
            created_at: n.created_at,
            count: 1,
            unread_count: n.is_read ? 0 : 1,
            subRows: [],
            notification: n,
          })
        ),
        pagination: data.pagination,
      };
    },
  });
}
